import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, BookOpen, Mic, MessageCircle, Lightbulb, Volume2, Type, Shuffle, Puzzle } from 'lucide-react';
import { ScrollPopup } from '../components/Popups';
import { useT } from '../context/LanguageContext';

const ACTIVITY_LINKS = {
  'Compréhension écrite': '/speaking-lab/comprehension-ecrite',
  "Apprendre par l'image": '/speaking-lab/apprendre-par-image',
  'Jeux de réflexion linguistique': '/speaking-lab/jeux-de-reflexion',
  'Jeux de réflexion (B2)': '/speaking-lab/jeux-reflexion-b2',
  'Réagis vite !': '/speaking-lab/reagis-vite',
  'Lis & Réagis': '/speaking-lab/lis-et-reagis',
  'Remets en ordre': '/speaking-lab/remets-en-ordre',
  'Réflexes Orthographiques': '/speaking-lab/reflexes-orthographiques',
  'Les Sons du Français': '/speaking-lab/les-sons-du-francais',
  'Reconstruis la phrase': '/speaking-lab/reconstruis-la-phrase',
  'Parle avec des indices': '/speaking-lab/parle-avec-des-indices',
  'Expression orale guidée par thème': '/speaking-lab/expression-orale-guidee',
  'Expression orale libre sur un sujet': '/speaking-lab/expression-orale-libre',
  'Jeux de rôle (situations réelles)': '/speaking-lab/jeux-de-role',
  'Réflexes linguistiques': '/speaking-lab/scribble',
};

const SECTIONS = [
  {
    id: 'comprendre',
    title: 'COMPRENDRE',
    subtitle: 'Compréhension',
    description: 'Développer la compréhension orale et écrite avant la production.',
    icon: BookOpen,
    accent: '#3B82F6',
    activities: [
      { name: 'Compréhension écrite', desc: 'Read and understand written French passages' },
      { name: "Apprendre par l'image", desc: 'Learn vocabulary and expressions through visual context' },
      { name: 'Réagis vite !', desc: 'Réponds naturellement, pense en français' },
      { name: 'Réflexes linguistiques', desc: 'Réactions naturelles et défis de mots rapides' },
    ],
  },
  {
    id: 'atelier',
    title: "ATELIER D'EXPRESSION",
    subtitle: 'Expression',
    description: "Un studio d'expression orale — parlez, lisez, réagissez, créez.",
    icon: Mic,
    accent: '#D4AF37',
    activities: [
      { name: 'Lis & Réagis', desc: 'Lecture expressive, réaction personnelle et improvisation orale' },
      { name: 'Remets en ordre', desc: 'Développe ton réflexe syntaxique — réorganise les phrases' },
      { name: 'Réflexes Orthographiques', desc: 'Accents, accords, lettres muettes — affine ton intuition' },
      { name: 'Les Sons du Français', desc: 'Rythme, phonétique et musicalité de la langue' },
      { name: 'Reconstruis la phrase', desc: 'Reconstruction linguistique — assemble les fragments' },
    ],
  },
  {
    id: 'exprimer',
    title: "S'EXPRIMER",
    subtitle: 'Parler',
    description: 'Développer la capacité à parler avec aisance et spontanéité.',
    icon: MessageCircle,
    accent: '#10B981',
    activities: [
      { name: 'Parle avec des indices', desc: '80 missions orales A1 → B2 — opinion, narration, débats, contre-arguments, chrono adaptatif' },
      { name: 'Expression orale guidée par thème', desc: '40 thèmes A1 + A2 — apprends à structurer une présentation en 5 étapes (plan, idées, enregistrement, feedback)' },
      { name: 'Expression orale libre sur un sujet', desc: '109 prompts A1 → B2 — écoute, message vocal, débat AI, actualité, mini conférence. Pas de plan, parole spontanée.' },
      { name: 'Jeux de rôle (situations réelles)', desc: '80 situations A1 → B2 — défendre, convaincre, négocier, débattre. L\'AI joue un interlocuteur réaliste.' },
    ],
  },
  {
    id: 'reflechir',
    title: 'RÉFLÉCHIR & JOUER',
    subtitle: 'Explorer',
    description: 'Apprendre en réfléchissant et en jouant avec la langue.',
    icon: Lightbulb,
    accent: '#A855F7',
    activities: [
      { name: 'Jeux de réflexion linguistique', desc: 'Brain teasers and logic puzzles using French language' },
      { name: 'Jeux de réflexion (B2)', desc: 'Argumentation, nuance et communication avancée — DELF B2' },
    ],
  },
];

const SpeakingLab = () => {
  const [activeSection, setActiveSection] = useState('comprendre');
  const panelRef = useRef(null);
  const t = useT();

  const activeData = SECTIONS.find(s => s.id === activeSection);

  const handleSectionClick = useCallback((sectionId) => {
    setActiveSection(sectionId);
    setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, []);

  return (
    <div className="bg-[#FAF7F0] min-h-screen">

      {/* Hero */}
      <section className="pt-16 pb-12" data-testid="speaking-lab-hero">
        <div className="max-w-[800px] mx-auto px-8 text-center">
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="speaking-lab-title">
            {t({ fr: 'Le Speaking Lab', en: 'The Speaking Lab' })}
          </h1>
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-10 bg-[#D4AF37]/50"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50"></div>
            <div className="h-px w-10 bg-[#D4AF37]/50"></div>
          </div>
          <p className="text-sm text-[#6B4E37] max-w-lg mx-auto leading-relaxed">
            {t({
              fr: "Pratiquez le français parlé à travers des activités structurées conçues pour développer aisance, précision et confiance.",
              en: 'Practice spoken French through structured activities designed to build fluency, accuracy, and confidence.',
            })}
          </p>
          <p className="text-xs text-[#999] max-w-md mx-auto mt-2 italic" data-testid="speaking-lab-guidance">
            {t({
              fr: "Après vous être entraîné(e) ici, essayez de parler avec un professeur ou rejoignez les conversations en direct.",
              en: 'After practicing here, try speaking with a teacher or join live conversations.',
            })}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20" data-testid="section-tabs-area">
        <div className="max-w-[960px] mx-auto px-8">

          {/* Section Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="section-tabs">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className="text-left relative rounded-xl p-5 transition-all duration-300 overflow-hidden group"
                  style={{
                    background: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                    border: isActive ? `2px solid ${section.accent}` : '2px solid #E8E4DC',
                    boxShadow: isActive ? `0 4px 20px ${section.accent}15` : 'none',
                  }}
                  data-testid={`tab-${section.id}`}>
                  {/* Accent strip */}
                  <div
                    className="absolute top-0 left-0 w-full h-0.5 transition-all duration-300"
                    style={{ background: isActive ? section.accent : 'transparent' }}
                  />
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 transition-all duration-300"
                    style={{
                      background: isActive ? `${section.accent}15` : '#FAF7F0',
                      color: isActive ? section.accent : '#6B4E37',
                    }}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <p
                    className="text-xs font-bold tracking-wider mb-0.5 transition-colors duration-300"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: isActive ? '#1A2F4A' : '#6B4E37',
                    }}>
                    {section.title}
                  </p>
                  <p className="text-[10px] text-[#999] leading-tight hidden sm:block">
                    {section.description.split(' ').slice(0, 5).join(' ')}...
                  </p>
                </button>
              );
            })}
          </div>

          {/* Active Panel */}
          {activeData && (
            <div
              ref={panelRef}
              key={activeData.id}
              className="rounded-2xl overflow-hidden transition-all duration-300"
              style={{
                background: '#fff',
                border: `1px solid ${activeData.accent}20`,
                boxShadow: `0 2px 16px ${activeData.accent}08`,
              }}
              data-testid={`panel-${activeData.id}`}>

              {/* Panel Header */}
              <div
                className="px-8 py-6"
                style={{ borderBottom: `1px solid ${activeData.accent}15` }}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${activeData.accent}12`, color: activeData.accent }}>
                    <activeData.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2
                      className="text-lg font-bold text-[#1A2F4A]"
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                      {activeData.title}
                    </h2>
                    <p className="text-xs text-[#6B4E37] italic">{activeData.description}</p>
                  </div>
                </div>
              </div>

              {/* Sub-topics */}
              <div className="p-6">
                <p className="text-[10px] text-[#999] uppercase tracking-widest mb-4 font-semibold">
                  {activeData.activities.length} {t({ fr: 'activités', en: 'activities' })}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeData.activities.map((act, i) => {
                    const link = ACTIVITY_LINKS[act.name];
                    const card = (
                      <div className="flex items-start gap-3.5 w-full">
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
                          style={{
                            background: link ? activeData.accent : '#F3F4F6',
                            color: link ? '#fff' : '#999',
                          }}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${link ? 'text-[#1A2F4A]' : 'text-[#999]'}`}>
                            {act.name}
                          </p>
                          <p className="text-[11px] text-[#6B4E37] mt-0.5 leading-snug">{act.desc}</p>
                        </div>
                        {link ? (
                          <ArrowRight className="w-4 h-4 flex-shrink-0 mt-1 transition-transform group-hover:translate-x-0.5" style={{ color: activeData.accent }} />
                        ) : (
                          <span className="text-[9px] text-[#999] border border-[#E8E4DC] rounded-full px-2 py-0.5 flex-shrink-0 mt-1 whitespace-nowrap">
                            {t({ fr: 'bientôt', en: 'soon' })}
                          </span>
                        )}
                      </div>
                    );

                    return link ? (
                      <Link
                        key={i}
                        to={link}
                        onClick={() => window.scrollTo(0, 0)}
                        className="group block rounded-xl p-4 transition-all duration-200 hover:shadow-md border border-transparent hover:border-[#E8E4DC]"
                        style={{ background: '#FAFAF8' }}
                        data-testid={`activity-${activeData.id}-${i}`}>
                        {card}
                      </Link>
                    ) : (
                      <div
                        key={i}
                        className="rounded-xl p-4 border border-[#F3F4F6]"
                        style={{ background: '#FAFCFB' }}
                        data-testid={`activity-${activeData.id}-${i}`}>
                        {card}
                      </div>
                    );
                  })}
                </div>

                {/* Section CTA */}
                <div className="mt-6 pt-5 border-t border-[#F3F4F6] flex items-center justify-between">
                  <p className="text-xs text-[#999] italic">
                    {activeData.activities.filter(a => ACTIVITY_LINKS[a.name]).length > 0
                      ? t({ fr: 'Cliquez sur une activité pour commencer.', en: 'Click an activity to begin practice.' })
                      : t({ fr: 'De nouvelles activités sont en préparation pour cette section.', en: 'New activities are being prepared for this section.' })}
                  </p>
                  {activeData.activities.some(a => ACTIVITY_LINKS[a.name]) && (
                    <Link
                      to={ACTIVITY_LINKS[activeData.activities.find(a => ACTIVITY_LINKS[a.name]).name]}
                      className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-200 hover:shadow-md"
                      style={{
                        background: activeData.accent,
                        color: '#fff',
                      }}
                      data-testid="section-cta">
                      {t({ fr: 'Parler maintenant', en: 'Speak now' })}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E8E4DC] py-10">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-[#D4AF37] shadow flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_parlezmoifrench/artifacts/25ispvfj_selora%20logo-removebg.jpg"
                  alt="Selora Logo"
                  className="w-14 h-14 object-cover scale-125"
                />
              </div>
              <div>
                <span className="text-xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  SELORA
                </span>
                <p className="text-xs text-[#6B4E37]">French Academy</p>
              </div>
            </div>
            <p className="text-[#6B4E37] text-sm">
              {t({ fr: '© 2026 Selora French Academy. Tous droits réservés.', en: '© 2026 Selora French Academy. All rights reserved.' })}
            </p>
          </div>
        </div>
      </footer>
      <ScrollPopup />
    </div>
  );
};

export default SpeakingLab;
